import { apiErrorText } from "../../api/api-error.js";
import { portalText } from "../../i18n/portal.js";
/**
 * The trunk dialog's form, as data (plan-008 ST-UN, design §22.14, R-UI-2). Pure, so the rules a
 * member meets in the dialog are tested without a DOM.
 *
 * **The dialog validates with the API's own function.** `validateSipTrunkInput` is what the route
 * runs (`packages/domain`, R-TRK-4); the dialog runs it on the same snake_case body before sending,
 * so every field error is known — and worded plainly next to its field — before anything leaves the
 * browser. The API still validates; a refusal it adds (a number already on another account's trunk)
 * is placed at the field it names.
 *
 * **The password is write-only.** The form's `password` starts empty on create and on edit, is
 * never filled from a trunk (a {@link SipTrunkView} has no password to fill it from) and is sent
 * only when typed. On edit an empty field means "keep the current one".
 *
 * **An inbound-only trunk (plan-015 SIP, ADR 0046).** Its edit form opens with the outbound half empty (host,
 * port, username, password, countries). Left empty, the trunk stays receiving only and is checked with the
 * domain's inbound-only rules (`validateSipTrunkCreate` on a body naming none of that half); filled in, it is
 * checked as a two-way trunk — the password then required — and saved in one `updateSipTrunk`, as the API asks.
 */
import { ApiError } from "@wetalk/api-client";
import {
  SIP_TRUNK_OUTBOUND_FIELD,
  isE164,
  isSipTrunkInputRejection,
  toWireNumber,
  validateSipTrunkCreate,
  validateSipTrunkInput,
  validateSipTrunkPassword,
  type CallerIdPresentation,
  type SipCodecPolicy,
  type SipInboundDelivery,
  type SipMediaEncryption,
  type SipTransport,
  type SipTrunkAnyInput,
  type SipTrunkAnySettingInput,
  type SipTrunkFieldError,
  type SipTrunkSettingInput,
  type WireNumberFormat,
} from "@wetalk/domain";

import { SipTrunkMaximumReachedError, isInboundOnlyTrunkView, type SipTrunkView } from "../../api/slice/sip-trunk.js";
import { FIELD_ERROR_WORD, INBOUND_ONLY_CALLER_ID_WORD, trunkMaximumSentence } from "./sip-trunk-copy.js";

export interface TrunkFormNumber {
  /** A stable React key for the row. */
  readonly key: string;
  /** The saved number's id on edit; `null` for a row added in this dialog. */
  readonly phoneNumberId: string | null;
  readonly e164: string;
  readonly isCallerId: boolean;
}

export interface TrunkForm {
  readonly displayName: string;
  readonly host: string;
  /** As typed, so "50 60" can be refused with words rather than coerced. */
  readonly port: string;
  readonly transport: SipTransport;
  readonly mediaEncryption: SipMediaEncryption;
  readonly authUsername: string;
  /** Empty unless typed — on create it is required, on edit empty keeps the current one. */
  readonly password: string;
  readonly number: readonly TrunkFormNumber[];
  /** One address or CIDR per line. */
  readonly allowedAddress: string;
  readonly wireNumberFormat: WireNumberFormat;
  /** `""` for none. */
  readonly nationalCountry: string;
  readonly callerIdPresentation: CallerIdPresentation;
  readonly allowedDestinationCountry: readonly string[];
  readonly codecPolicy: SipCodecPolicy;
  /** How the provider sends calls (plan-008 ST-KM): `direct` unless the member chose registration. */
  readonly inboundDelivery: SipInboundDelivery;
}

/** Where a field error is drawn: one key per control, one per number row. */
export type TrunkFormErrorKey =
  | "display_name"
  | "host"
  | "port"
  | "transport"
  | "media_encryption"
  | "auth_username"
  | "password"
  | "number"
  | `number:${number}`
  | "allowed_address"
  | "wire_number_format"
  | "national_country"
  | "caller_id_presentation"
  | "allowed_destination_country"
  | "codec_policy"
  | "inbound_delivery"
  | "form";

export type TrunkFormErrors = Readonly<Partial<Record<TrunkFormErrorKey, string>>>;

let rowSeed = 0;

/** A fresh row key; unique within the page. */
export function nextRowKey(): string {
  rowSeed += 1;
  return `row-${String(rowSeed)}`;
}

/**
 * A new trunk's starting values: the first reviewed country ticked (the launch set is Greece only),
 * standard SIP over UDP on 5060, HD first. No host, username or password is guessed.
 */
export function emptyTrunkForm(reviewedCountry: readonly string[]): TrunkForm {
  return {
    displayName: "",
    host: "",
    port: "5060",
    transport: "udp",
    mediaEncryption: "none",
    authUsername: "",
    password: "",
    number: [{ key: nextRowKey(), phoneNumberId: null, e164: "", isCallerId: true }],
    allowedAddress: "",
    wireNumberFormat: "e164_plus",
    nationalCountry: "",
    callerIdPresentation: "number_e164_plus",
    allowedDestinationCountry: reviewedCountry.slice(0, 1),
    codecPolicy: "wideband_first",
    inboundDelivery: "direct",
  };
}

/**
 * A saved trunk's values — every field but the password, which the trunk never carries. An inbound-only trunk's
 * outbound half opens empty (its view holds `""` and `0` there, never an address or a port).
 */
export function trunkFormOf(trunk: SipTrunkView): TrunkForm {
  const inboundOnly = isInboundOnlyTrunkView(trunk);
  return {
    displayName: trunk.displayName,
    host: trunk.host,
    port: inboundOnly ? "" : String(trunk.port),
    transport: trunk.transport,
    mediaEncryption: trunk.mediaEncryption,
    authUsername: trunk.authUsername,
    password: "",
    number: trunk.number.map((one) => ({
      key: nextRowKey(),
      phoneNumberId: one.phoneNumberId,
      e164: one.e164,
      isCallerId: one.isCallerId,
    })),
    allowedAddress: trunk.allowedAddress.join("\n"),
    wireNumberFormat: trunk.wireNumberFormat,
    nationalCountry: trunk.nationalCountry ?? "",
    callerIdPresentation: trunk.callerIdPresentation,
    allowedDestinationCountry: trunk.allowedDestinationCountry,
    codecPolicy: trunk.codecPolicy,
    inboundDelivery: trunk.inboundDelivery,
  };
}

function lines(text: string): string[] {
  return text
    .split(/[\n,]/)
    .map((one) => one.trim())
    .filter((one) => one.length > 0);
}

function portOf(text: string): unknown {
  const trimmed = text.trim();
  return /^[0-9]+$/.test(trimmed) ? Number(trimmed) : trimmed;
}

/** The `SipTrunkCreate` body the form describes, exactly as the route would receive it. */
export function trunkBodyOf(form: TrunkForm, password: string): Record<string, unknown> {
  return {
    display_name: form.displayName.trim(),
    host: form.host.trim(),
    port: portOf(form.port),
    transport: form.transport,
    media_encryption: form.mediaEncryption,
    auth_username: form.authUsername.trim(),
    password,
    allowed_address: lines(form.allowedAddress),
    wire_number_format: form.wireNumberFormat,
    national_country: form.nationalCountry === "" ? null : form.nationalCountry,
    caller_id_presentation: form.callerIdPresentation,
    allowed_destination_country: [...form.allowedDestinationCountry],
    codec_policy: form.codecPolicy,
    inbound_delivery: form.inboundDelivery,
    number: form.number.map((one) => ({ e164: one.e164.trim(), is_caller_id: one.isCallerId })),
  };
}

/** The words for one field error, with the few rules whose generic word would be too vague. */
function wordsOf(error: SipTrunkFieldError, key: TrunkFormErrorKey): string {
  if (key === "port" && (error.code === "out_of_range" || error.code === "wrong_type")) {
    return portalText("operations", "enter_a_port_number_between_1_and_65535_usually_5060_or_50_3db6e8d7");
  }
  if (key === "allowed_destination_country" && (error.code === "empty" || error.code === "required")) {
    return portalText("operations", "choose_at_least_one_country_9d5f1897");
  }
  if (key === "number" && (error.code === "empty" || error.code === "required")) {
    return portalText("operations", "add_at_least_one_number_7b02b0db");
  }
  return FIELD_ERROR_WORD[error.code];
}

/** `number[2].e164` → `number:2`; `allowed_address[1]` → `allowed_address`; unknown → `form`. */
export function errorKeyOf(field: string): TrunkFormErrorKey {
  const row = /^number\[(\d+)\]/.exec(field);
  if (row !== null) return `number:${Number(row[1])}`;
  const bare = field.replace(/\[\d+\]$/, "");
  const known: readonly TrunkFormErrorKey[] = [
    "display_name",
    "host",
    "port",
    "transport",
    "media_encryption",
    "auth_username",
    "password",
    "number",
    "allowed_address",
    "wire_number_format",
    "national_country",
    "caller_id_presentation",
    "allowed_destination_country",
    "codec_policy",
    "inbound_delivery",
  ];
  return known.find((one) => one === bare) ?? "form";
}

function errorsOf(error: readonly SipTrunkFieldError[]): TrunkFormErrors {
  const out: Partial<Record<TrunkFormErrorKey, string>> = {};
  for (const one of error) {
    const key = errorKeyOf(one.field);
    if (out[key] !== undefined) continue;
    const line = /^allowed_address\[(\d+)\]$/.exec(one.field);
    const words = wordsOf(one, key);
    out[key] = line === null ? words : portalText("operations", "line_value_value_d2f9bda0", { v0: String(String(Number(line[1]) + 1)), v1: String(words) });
  }
  return out;
}

export type TrunkFormVerdict =
  | { readonly kind: "valid"; readonly input: SipTrunkAnyInput }
  | { readonly kind: "invalid"; readonly error: TrunkFormErrors };

/** Whether the form leaves the whole outbound half empty: host, port, username, password and countries. */
export function outboundLeftEmpty(form: TrunkForm): boolean {
  return (
    form.host.trim() === "" &&
    form.port.trim() === "" &&
    form.authUsername.trim() === "" &&
    form.password === "" &&
    form.allowedDestinationCountry.length === 0
  );
}

/** The inbound-only body: the form's settings with none of the outbound half named (that absence is the shape). */
function inboundOnlyBodyOf(form: TrunkForm): Record<string, unknown> {
  const body = trunkBodyOf(form, "");
  for (const field of SIP_TRUNK_OUTBOUND_FIELD) delete body[field];
  return body;
}

/**
 * The form checked with the domain's rules. On edit an empty password is allowed (it keeps the
 * current one), so a placeholder stands in for the validator's required password and the typed one,
 * when there is one, is checked on its own. Editing an inbound-only trunk (`inboundOnlyTrunk`), an empty
 * outbound half keeps it so — and no number may then present caller ID — while a filled one needs it all,
 * the password included.
 */
export function verdictOf(form: TrunkForm, mode: "create" | "edit", inboundOnlyTrunk = false): TrunkFormVerdict {
  if (mode === "edit" && inboundOnlyTrunk) {
    if (outboundLeftEmpty(form)) {
      const kept = validateSipTrunkCreate(inboundOnlyBodyOf(form));
      if (isSipTrunkInputRejection(kept)) return { kind: "invalid", error: errorsOf(kept.error) };
      const callerId = form.number.findIndex((one) => one.isCallerId);
      if (callerId >= 0) return { kind: "invalid", error: { [`number:${String(callerId)}`]: INBOUND_ONLY_CALLER_ID_WORD() } };
      return { kind: "valid", input: kept };
    }
    const added = validateSipTrunkInput(trunkBodyOf(form, form.password));
    if (isSipTrunkInputRejection(added)) return { kind: "invalid", error: errorsOf(added.error) };
    return { kind: "valid", input: added };
  }
  const typed = form.password;
  const keepsPassword = mode === "edit" && typed.length === 0;
  const checked = validateSipTrunkInput(trunkBodyOf(form, keepsPassword ? "unchanged-placeholder" : typed));
  if (isSipTrunkInputRejection(checked)) return { kind: "invalid", error: errorsOf(checked.error) };
  if (mode === "edit" && !keepsPassword) {
    const password = validateSipTrunkPassword({ password: typed });
    if (isSipTrunkInputRejection(password)) return { kind: "invalid", error: errorsOf(password.error) };
  }
  return { kind: "valid", input: checked };
}

/**
 * The settings an edit changed — a PATCH names only those (a connection field re-verifies). An input with no
 * outbound half (an inbound-only trunk kept so) names none of it; one that adds it names all of it.
 */
export function settingChangeOf(trunk: SipTrunkView, input: SipTrunkAnySettingInput): Partial<SipTrunkSettingInput> {
  const change: { -readonly [K in keyof SipTrunkSettingInput]?: SipTrunkSettingInput[K] } = {};
  const same = (a: readonly string[], b: readonly string[]): boolean =>
    a.length === b.length && a.every((one, index) => one === b[index]);
  if (input.displayName !== trunk.displayName) change.displayName = input.displayName;
  if (input.host !== null && input.host !== trunk.host) change.host = input.host;
  if (input.port !== null && input.port !== trunk.port) change.port = input.port;
  if (input.transport !== trunk.transport) change.transport = input.transport;
  if (input.mediaEncryption !== trunk.mediaEncryption) change.mediaEncryption = input.mediaEncryption;
  if (input.authUsername !== null && input.authUsername !== trunk.authUsername) change.authUsername = input.authUsername;
  if (!same(input.allowedAddress, trunk.allowedAddress)) change.allowedAddress = input.allowedAddress;
  if (input.wireNumberFormat !== trunk.wireNumberFormat) change.wireNumberFormat = input.wireNumberFormat;
  if (input.nationalCountry !== trunk.nationalCountry) change.nationalCountry = input.nationalCountry;
  if (input.callerIdPresentation !== trunk.callerIdPresentation) change.callerIdPresentation = input.callerIdPresentation;
  if (input.allowedDestinationCountry !== null && !same(input.allowedDestinationCountry, trunk.allowedDestinationCountry)) {
    change.allowedDestinationCountry = input.allowedDestinationCountry;
  }
  if (input.codecPolicy !== trunk.codecPolicy) change.codecPolicy = input.codecPolicy;
  if (input.inboundDelivery !== trunk.inboundDelivery) change.inboundDelivery = input.inboundDelivery;
  return change;
}

/** A connection field changed: the trunk goes back to "Not verified yet" (§22.13.2). */
export function editsConnection(change: Partial<SipTrunkSettingInput>): boolean {
  return (
    change.host !== undefined ||
    change.port !== undefined ||
    change.transport !== undefined ||
    change.authUsername !== undefined
  );
}

/**
 * The live example for a number format: the first number as it would be written on the wire, or
 * `null` when there is no valid first number yet (or the national form has no country).
 */
export function wireExampleOf(form: TrunkForm, format: WireNumberFormat): string | null {
  const first = form.number[0]?.e164.trim() ?? "";
  if (!isE164(first)) return null;
  try {
    return toWireNumber(first, format, form.nationalCountry === "" ? null : form.nationalCountry);
  } catch {
    return null;
  }
}

/** Whether the national-country choice is needed by the chosen format or caller-ID presentation. */
export function needsNationalCountry(form: TrunkForm): boolean {
  return form.wireNumberFormat === "national" || form.callerIdPresentation === "number_national";
}

/**
 * An API refusal placed where the member will see it: at its named field, or at the top. The trunk
 * maximum (`409 sip_trunk_limit_reached`, plan-008 ST-RV2) is said at the top in plain words, with the
 * number only when the API sent it.
 */
export function apiRefusalErrors(error: unknown, fallback: string): TrunkFormErrors {
  if (error instanceof ApiError && error.code === "sip_trunk_limit_reached") {
    return { form: trunkMaximumSentence(error instanceof SipTrunkMaximumReachedError ? error.maximum : null) };
  }
  if (error instanceof ApiError) {
    const key = error.field === null ? "form" : errorKeyOf(error.field === "e164" ? "number" : error.field);
    const out: Partial<Record<TrunkFormErrorKey, string>> = {};
    out[key] = apiErrorText(error, "");
    return out;
  }
  return { form: error instanceof Error ? apiErrorText(error, "") : fallback };
}
